import React from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import { Globe } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart';

interface CountryData {
  country: string;
  count: number;
}

interface CountryBreakdownChartProps {
  data: CountryData[];
  totalScans: number;
  className?: string;
}

// Country code to flag emoji mapping
const getCountryFlag = (countryCode: string): string => {
  const code = countryCode.toUpperCase();
  // Convert country code to flag emoji
  if (code.length === 2) {
    const offset = 127397;
    const codePoints = code.split('').map(c => c.charCodeAt(0) + offset);
    return String.fromCodePoint(...codePoints);
  }
  return '';
};

// Country code to name mapping (common ones)
const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  CA: 'Canada',
  AU: 'Australia',
  DE: 'Germany',
  FR: 'France',
  JP: 'Japan',
  CN: 'China',
  IN: 'India',
  BR: 'Brazil',
  MX: 'Mexico',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  RU: 'Russia',
  KR: 'South Korea',
  SG: 'Singapore',
  AE: 'UAE',
  SA: 'Saudi Arabia',
  ZA: 'South Africa',
  NZ: 'New Zealand',
  IE: 'Ireland',
  CH: 'Switzerland',
  AT: 'Austria',
  BE: 'Belgium',
  PK: 'Pakistan',
};

const getCountryName = (code: string): string => {
  const upperCode = code.toUpperCase();
  return COUNTRY_NAMES[upperCode] || code;
};

// Color gradient for bars (warm coral theme)
const GRADIENT_COLORS = [
  '#D97756',
  '#E8927C',
  '#EBA896',
  '#F0BEB0',
  '#F5D4CA',
  '#FAE9E4',
  '#FDF5F2',
];

const chartConfig = {
  scans: {
    label: "Scans",
    color: "#D97756",
  },
};

export function CountryBreakdownChart({ data, totalScans, className = '' }: CountryBreakdownChartProps) {
  // Sort and take top 7 countries
  const chartData = [...data]
    .sort((a, b) => b.count - a.count)
    .slice(0, 7)
    .map((item, index) => ({
      country: item.country,
      countryName: getCountryName(item.country),
      flag: getCountryFlag(item.country),
      scans: item.count,
      percentage: totalScans > 0 ? ((item.count / totalScans) * 100).toFixed(1) : '0',
      color: GRADIENT_COLORS[index] || GRADIENT_COLORS[GRADIENT_COLORS.length - 1],
    }));

  if (!data || data.length === 0) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#2D2520] flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#D97756]" />
            Geographic Breakdown
          </h3>
        </div>
        <p className="text-xs text-[#5C524A] mb-3">Top countries by scans</p>
        <div className="flex flex-col items-center justify-center h-48 bg-[#F5F0EB] rounded-lg border border-dashed border-[#E5DED6]">
          <Globe className="w-10 h-10 text-[#9A8F85] mb-2" />
          <p className="text-sm text-[#5C524A]">No geographic data yet</p>
          <p className="text-xs text-[#9A8F85] mt-1">Location data will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[#2D2520] flex items-center gap-2">
          <Globe className="w-4 h-4 text-[#D97756]" />
          Geographic Breakdown
        </h3>
        <span className="text-xs text-[#5C524A]">
          {data.length} {data.length === 1 ? 'country' : 'countries'}
        </span>
      </div>
      <p className="text-xs text-[#5C524A] mb-3">Top countries by scans</p>

      <div className="w-full overflow-hidden">
        <ChartContainer config={chartConfig} className="h-48 w-full">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 40, left: 10, bottom: 5 }}
          >
            <XAxis
              type="number"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#5C524A' }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <YAxis
              type="category"
              dataKey="countryName"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#5C524A' }}
              width={80}
              tickFormatter={(value: string) => {
                const item = chartData.find(d => d.countryName === value);
                return item ? `${item.flag} ${value.length > 10 ? value.slice(0, 10) + '...' : value}` : value;
              }}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name) => {
                    const item = chartData.find(d => d.scans === value);
                    return [`${value} scans (${item?.percentage || 0}%)`, ''];
                  }}
                  labelFormatter={(label) => {
                    const item = chartData.find(d => d.countryName === label);
                    return item ? `${item.flag} ${item.countryName}` : label;
                  }}
                />
              }
            />
            <Bar
              dataKey="scans"
              radius={[0, 4, 4, 0]}
              animationBegin={200}
              animationDuration={1200}
              animationEasing="ease-out"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>

      {/* Quick stats */}
      <div className="mt-3 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="text-[#5C524A]">Top:</span>
          <span className="font-medium text-[#2D2520]">
            {chartData[0]?.flag} {chartData[0]?.countryName}
          </span>
        </div>
        <span className="text-[#5C524A]">
          {chartData[0]?.percentage}% of traffic
        </span>
      </div>
    </div>
  );
}
